import React from 'react';
import { useNavigate } from 'react-router-dom';
import { VEHICLES } from '../constants';
import { RoutePath } from '../types';
import { Check, Users, Briefcase } from 'lucide-react';
import { motion } from 'framer-motion';

const Fleet: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="pt-32 bg-black min-h-screen">
      <div className="px-6 lg:px-12 mb-20">
        <motion.h1 
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="font-serif text-5xl md:text-8xl text-white mb-6"
        >
          Notre Flotte Limousine
        </motion.h1>
        <motion.p 
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-lg md:text-xl text-slate-400 font-light max-w-2xl"
        >
           Voyagez à bord de véhicules de prestige rigoureusement entretenus. LimoHass propose une flotte exclusivement signée Mercedes-Benz, garantissant un service de chauffeur privé irréprochable entre Genève et Lausanne.
        </motion.p>
      </div>

      <div className="flex flex-col gap-0">
        {VEHICLES.map((vehicle, index) => (
          <motion.div 
            key={vehicle.id} 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-10%" }}
            className="grid grid-cols-1 lg:grid-cols-2 border-t border-white/10 min-h-[80vh] overflow-hidden"
          >
            {/* Image Section */}
            <motion.div 
               variants={{
                 hidden: { opacity: 0, scale: 1.1 },
                 visible: { opacity: 1, scale: 1, transition: { duration: 1, ease: "easeOut" } }
               }}
               className={`relative h-[40vh] lg:h-auto overflow-hidden ${index % 2 === 1 ? 'lg:order-2' : ''}`}
            >
               <img 
                 src={vehicle.image} 
                 alt={`Location ${vehicle.name} avec chauffeur`} 
                 className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-1000"
               />
               <div className="absolute inset-0 bg-black/20"></div>
            </motion.div>

            {/* Info Section */}
            <motion.div 
               variants={{
                 hidden: { opacity: 0, x: index % 2 === 1 ? 50 : -50 },
                 visible: { opacity: 1, x: 0, transition: { duration: 0.8, delay: 0.2 } }
               }}
               className={`p-8 lg:p-24 flex flex-col justify-center bg-[#050505] ${index % 2 === 1 ? 'lg:order-1' : ''}`}
            >
               <span className="text-gold-500 font-mono text-xs tracking-widest mb-4 block uppercase">{vehicle.category} Class</span>
               <h2 className="font-serif text-3xl md:text-5xl text-white mb-8">{vehicle.name}</h2>
               
               <p className="text-slate-400 text-base md:text-lg leading-relaxed mb-12 font-light">
                  {vehicle.description}
               </p>

               <div className="grid grid-cols-2 gap-8 mb-12 border-y border-white/10 py-8">
                  <div>
                     <span className="block text-slate-500 text-xs uppercase tracking-wider mb-2">Passagers</span>
                     <div className="flex items-center text-white text-xl gap-2">
                        <Users size={20} className="text-gold-500" /> {vehicle.passengers}
                     </div>
                  </div>
                  <div>
                     <span className="block text-slate-500 text-xs uppercase tracking-wider mb-2">Bagages</span>
                     <div className="flex items-center text-white text-xl gap-2">
                        <Briefcase size={20} className="text-gold-500" /> {vehicle.luggage}
                     </div>
                  </div>
               </div>

               <ul className="space-y-4 mb-12">
                  {vehicle.features.map((feature, i) => (
                     <li key={i} className="flex items-center text-slate-300 text-sm md:text-base">
                        <Check size={16} className="text-gold-500 mr-4 flex-shrink-0" />
                        <span className="font-light">{feature}</span>
                     </li>
                  ))}
               </ul>

               <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate(`${RoutePath.BOOKING}?vehicle=${vehicle.id}`)}
                  className="self-start border border-white text-white px-10 py-4 text-xs md:text-sm font-bold uppercase tracking-widest hover:bg-white hover:text-black transition-all"
               >
                  Réserver {vehicle.name}
               </motion.button>
            </motion.div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default Fleet;